import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { CardSignatureUI } from "./ui"
import { FieldsErrorType, ICardSignatureContainer, ICardSignatureUI, IFieldsError } from "./types"

import { useValue } from "@hooks/useValue"
import { useFieldsErrorAnimation } from "@hooks/useFieldsErrorAnimation"
import { validFormData } from "@hooks/useForm"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { useContractsSignature } from "@api/signature/state"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


type SignatureFormState = {
    commentCallCenter: string
    contractStatusCallCenterID: number | ''
    dateNextCall: string
    timeNextCall: string
}

const EMPTY_SIGNATURE_FORM_STATE: SignatureFormState = {
    commentCallCenter: '',
    contractStatusCallCenterID: 1,
    dateNextCall: '',
    timeNextCall: '',
}

const createSignatureFormSnapshot = (state: SignatureFormState) => JSON.stringify(state)

/** Контейнер анкеты - Договор на подпись */
export const CardSignatureContainer = ({ isOpen, cardID, contractNumber, handleButtonOk, handleButtonCancel }: ICardSignatureContainer) => {
    const notify = useContext(NotificationContext)

    const currentCardID = useValue<number>(0)
    const dataBase = useValue<string>('')

    /** Под ошибки для полей */
    const fieldsError = useValue<IFieldsError>({})
    /** Активность кнопки сохранить */
    const isDisableBtnSave = useValue<boolean>(true)
    
    /** Идентификатор статуса */
    const txtStatusID = useValue<number | ''>(1)
    /** Наименование идентификатора */
    const txtStatusName = useValue<string>('Новый')
    /** Дата отложенного звонка */
    const txtDateNextCall = useValue<string>('')
    /** Время отложенного звонка */
    const txtTimeNextCall = useValue<string>('')
    /** Комментарий */
    const txtComment = useValue<string>('')

    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const savedFormSnapshotRef = useRef(createSignatureFormSnapshot(EMPTY_SIGNATURE_FORM_STATE))
    const hasSavedChangesRef = useRef(false)
    const isFormReadyRef = useRef(false)


    const { 
        isLoadingCard, 

        clearStateSignatureCard, actionGetSignatureCard, actionPutSignatureCard,
    } = useContractsSignature(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,

        clearStateSignatureCard: state.clearStateSignatureCard,
        actionGetSignatureCard: state.actionGetSignatureCard,
        actionPutSignatureCard: state.actionPutSignatureCard,
    })))

    const clearCard = () => {
        clearStateSignatureCard()
        currentCardID.setValue(0)
        dataBase.setValue('')
        txtStatusID.setValue(1)
        txtStatusName.setValue('Новый')
        txtDateNextCall.setValue('')
        txtTimeNextCall.setValue('')
        txtComment.setValue('')
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createSignatureFormSnapshot(EMPTY_SIGNATURE_FORM_STATE)
    }

    const getCurrentFormState = (): SignatureFormState => ({
        commentCallCenter: txtComment.value,
        contractStatusCallCenterID: txtStatusID.value,
        dateNextCall: txtDateNextCall.value,
        timeNextCall: txtTimeNextCall.value,
    })

    useEffect(() => {
        let isCurrentRequest = true
        const requestedCardID = Number(cardID)

        if (isOpen && requestedCardID > 0) {
            clearCard()
            currentCardID.setValue(requestedCardID)
            loadCardData(requestedCardID, () => isCurrentRequest)
        } else {
            clearCard()
        }

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createSignatureFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    
    // ================================================================================

    /** Загрузка данных по анкете */
    const loadCardData = (loginID: number, isCurrentRequest: () => boolean = () => true) => {
        actionGetSignatureCard(loginID).then((res) => {
            if (!isCurrentRequest()) return
            if (res.status === 400 || res.status === 403) return notify && notifyOpen(res.message, 'error', 2000, notify)

            if (res.status === 200) {
                const formState: SignatureFormState = {
                    commentCallCenter: res.data?.commentCallCenter || '',
                    contractStatusCallCenterID: res.data?.contractStatusCallCenterID || '',
                    dateNextCall: res.data?.dateNextCall || '',
                    timeNextCall: res.data?.timeNextCall || '',
                }

                dataBase.setValue(res.data?.dataBase || '')
                txtComment.setValue(formState.commentCallCenter)
                txtStatusID.setValue(formState.contractStatusCallCenterID)
                txtStatusName.setValue(res.data?.contractStatusCallCenterName || '')
                txtDateNextCall.setValue(formState.dateNextCall)
                txtTimeNextCall.setValue(formState.timeNextCall)
                savedFormSnapshotRef.current = createSignatureFormSnapshot(formState)
                isFormReadyRef.current = true
                isDisableBtnSave.setValue(true)
            }
        })
    }

    /** Функция по активации анимации ошибок в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handleButtonOk && handleButtonOk(true)
        } else {
            handleButtonCancel && handleButtonCancel()
        }
    }

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionType: string, actionID: number = 0) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            cancelButtonText: actionType === 'closeForm' ? 'Продолжить редактирование' : undefined,
            okButtonText: actionType === 'closeForm' ? 'Закрыть без сохранения' : undefined,

            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            handleButtonOk() {
                switch (actionType) {
                    case 'closeForm': { // Закрытие анкеты оператора
                        closeCard()
                        break
                    }
                }
            }
        })
    }


    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'statusName', value: txtStatusName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Статус не может быть пустым.'},
            {fieldName: 'dateNextCall', value: txtDateNextCall.value, validType: 'date', isRequired: txtStatusID.value === 2 ? true : false, errorMessage: 'Дата отложенного звонка не корректна или такой даты не существует.'},
            {fieldName: 'timeNextCall', value: txtTimeNextCall.value, validType: 'time', isRequired: false, errorMessage: 'Время отложенного звонка не корректное или такого времени не сущесвует.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        // Если все хорошо, то сохраняем данные
        if (validateData.length === 0) {
            const formState = getCurrentFormState()
            actionPutSignatureCard(currentCardID.value, {
                commentCallCenter: formState.commentCallCenter,
                contractStatusCallCenterID: Number(formState.contractStatusCallCenterID),
                dateNextCall: formState.dateNextCall,
                timeNextCall: formState.timeNextCall,
            }).then((res) => {
                if (res.status === 200) {
                    notify && notifyOpen('Данные успешно сохранены', 'success', 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal) {
                        closeCard()
                    } else {
                        savedFormSnapshotRef.current = createSignatureFormSnapshot(formState)
                        isDisableBtnSave.setValue(true)
                        loadCardData(currentCardID.value)
                    }
                } else {
                    notify && notifyOpen(res.message, 'warning', 1500, notify)
                }
            })
        }
    }
    
    /** Кнопка закрытия окна */
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
        } else {
            handleOpenModalConfirm(
                true, 
                'Сохранение формы', 
                '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?', 
                'closeForm'
            )
        }
    }


    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardSignatureUI = {
        isOpen, contractNumber: contractNumber, dataBase: dataBase.value, fieldsError,
        isLoadingCard, isDisableBtnSave,
        
        txtStatusID, txtStatusName, txtComment,
        txtDateNextCall, txtTimeNextCall, 

        handleBtnSave, handleBtnClose,
    }
    
    return <>
        <CardSignatureUI {...propsToComponent} />
        <ModalConfirm {...modalConfirm.value} />
    </>
}